// import components
import { Stack, styled, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import utils
import { getIconColor } from 'components/mrp/mrp.utils';

// import types
import type { SxProps, Theme } from '@mui/material';
import type { PlanningEntityType } from 'generated/index';

// The takenlijst grid follows the prescriptions grid so both read as the same table.
import { dataGridSx as prescriptionsDataGridSx } from 'components/roster-portal/care-receivers/components/prescriptions/PrescriptionRulesGrid.styles';

/** Marks the rows that declare a care receiver above its categories. */
export const CARE_RECEIVER_ROW_CLASS = 'backlog-care-receiver-row';

/** Marks the rows that summarise one task category for a care receiver. */
export const CATEGORY_ROW_CLASS = 'backlog-category-row';

/** Marks the pinned totals column, so a vertical divider can sit to its left. */
export const TOTAL_COLUMN_CLASS = 'backlog-total-col';

// The grid keeps its styling as an `sx` object rather than a styled component: RosterDataGrid forwards
// only `sx` (merging it into the grid's own styles) and takes no `className`, so `styled(RosterDataGrid)`
// would have nowhere to apply. It also mirrors the sibling `prescriptionsDataGridSx` this spreads from,
// so the two grids stay one table.
export const dataGridSx: SxProps<Theme> = {
  // Follows the prescriptions grid so both read as the same table. The cast is unavoidable:
  // prescriptionsDataGridSx is typed as the SxProps union, which cannot be spread without narrowing.
  ...(prescriptionsDataGridSx as Record<string, unknown>),
  // That grid hides the vertical scrollbar, which suits a short grid inside a card that is not meant
  // to scroll. This is a full page table that scrolls itself, so the scrollbar is shown again — only
  // display was overridden there, so restoring it lets MUI's own styling for the element apply.
  '& .MuiDataGrid-scrollbar--vertical': { display: 'revert' },
  // It fills its area and scrolls itself, which is what keeps the week headers in place — DataGridPro
  // pins them inside its own scroller — and what lets it virtualise its rows.
  height: '100%',
  // The inherited styles force every cell white with !important, so the bands have to be both more
  // specific and important to win.
  [`& .${CARE_RECEIVER_ROW_CLASS} .MuiDataGrid-cell`]: {
    backgroundColor: (theme: Theme) => `${theme.palette.grey[100]} !important`,
    borderTop: '1px solid',
    borderTopColor: (theme: Theme) => theme.palette.divider
  },
  [`& .${CARE_RECEIVER_ROW_CLASS}:hover .MuiDataGrid-cell`]: {
    backgroundColor: (theme: Theme) => `${theme.palette.grey[100]} !important`
  },
  // A category row sits between the band and the rules: no fill, just a divider above it.
  [`& .${CATEGORY_ROW_CLASS} .MuiDataGrid-cell`]: {
    borderTop: '1px solid',
    borderTopColor: (theme: Theme) => theme.palette.divider
  },
  // A vertical divider sets the pinned Totaal column apart from the week columns — on its header and
  // every cell, so the line runs the full height of the grid.
  [`& .${TOTAL_COLUMN_CLASS}`]: {
    borderLeft: '1px solid',
    borderLeftColor: (theme: Theme) => theme.palette.divider
  }
};

export const LabelCell = styled(Stack)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '100%',
  width: '100%',
  minWidth: 0
});

/** Rules are indented so they read as belonging to the category above them. */
export const RuleLabelCell = styled(LabelCell)(({ theme }) => ({
  paddingLeft: theme.spacing(3)
}));

/**
 * Both header levels — the care receiver band and the category line — are bold and sized as body1,
 * while the rules below them render as body2. The size comes from the variant rather than a literal
 * so the three levels cannot drift apart: this theme's body1 is 0.857rem and body2 is 0.75rem.
 */
export const HeaderLabel = styled(Typography)({
  fontWeight: 600
});

/**
 * The category label is plain text — it is a heading for the rules below it, not a control. Only the
 * chevron next to it is clickable.
 */
export const CategoryLabelCell = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: '100%',
  width: '100%',
  minWidth: 0
}));

export const CategoryCount = styled('span')(({ theme }) => ({
  fontSize: '0.6875rem',
  color: theme.palette.text.secondary,
  flexShrink: 0
}));

/** Groups the planning-entity symbol and the name together on the left of the care receiver band, so
 * the space-between band keeps the prescriptions link on the far right. */
export const CareReceiverNameGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0
}));

/**
 * The planning-entity symbol shown before an entity header's name, drawn in afsprakenplanning's own
 * per-type colour (via getIconColor) so a client, employee, resource or workplace each reads the same
 * as it does there. `entityType` drives the colour; the glyph itself is passed as the Icon `type`.
 */
export const EntityIcon = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'entityType'
})<{ entityType: PlanningEntityType }>(({ entityType }) => ({
  width: 16,
  flexShrink: 0,
  color: getIconColor(entityType)
}));

/** The expand/collapse chevron on a task-category row. */
export const CategoryChevronIcon = styled(Icon)({
  width: 14
});

/** The note marker beside a rule that carries a description. */
export const RuleNoteIcon = styled(Icon)({
  width: 18
});

/**
 * An outside-treatment week cell that opens the week-detail on click. The badge inside must fill the
 * cell exactly like a tekort/overschot cell, so this wrapper stretches its child rather than centring
 * it (centring would shrink the badge to its content); the only added chrome is a pointer cursor.
 */
export const OpenWeekCell = styled('div')(() => ({
  display: 'flex',
  height: '100%',
  width: '100%',
  cursor: 'pointer',
  '& > *': { flex: 1 }
}));
